// src/supabaseClient.js
import { createClient as createSupabaseClient } from '@supabase/supabase-js'

const URL = process.env.REACT_APP_SUPABASE_URL
const KEY = process.env.REACT_APP_SUPABASE_ANON_KEY
const STORAGE_KEY = 'sb-sayberry-auth'

// 환경변수 가드(선택): 빠지면 런타임 경고
if (!URL || !KEY) {
  // 개발 중에만 띄우는 경고 수준 로그
  // 배포 환경에서는 빌드타임에 보통 셋업되므로 실제로는 잘 안 뜸
  console.warn('[Supabase/cbraids] Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY')
}

// --- cookie helpers ---
function readCookie(key) {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]+)`))
  return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(key, value, maxAgeSec = 604800) {
  if (typeof document === 'undefined') return
  document.cookie = [
    `${key}=${encodeURIComponent(value)}`,
    'Path=/',
    'Domain=.sayberrygames.com',
    `Max-Age=${maxAgeSec}`,
    'SameSite=Lax',
    'Secure',
  ].join('; ')
}

function removeCookie(key) {
  writeCookie(key, '', 0)
}

function cleanupLegacyKeys() {
  ;['sb-auth-token', 'sb-cwaybmmpgkuasxbuxtqx-auth-token', 'sb-cwaybmmpgkuasxbuxtqx-auth-token-code-verifier']
    .forEach(k => {
      removeCookie(k)
      console.log(`[Cleanup/cbraids] Removed old cookie: ${k}`)
    })
}

// base64 prefix -> plain JSON migration (1회성)
function normalizeAuthCookieOnce() {
  const raw = readCookie(STORAGE_KEY)
  if (!raw) return

  // 이미 JSON이면 패스
  try {
    JSON.parse(raw)
    return
  } catch {}

  // base64-면 디코드 후 평문 JSON으로 재저장
  if (raw.startsWith('base64-')) {
    try {
      const decoded = atob(raw.slice(7))
      JSON.parse(decoded)
      writeCookie(STORAGE_KEY, decoded)
      console.log('[CookieStorage/cbraids] Migrated base64→json')
    } catch (e) {
      console.warn('[CookieStorage/cbraids] Migration failed, removing', e)
      removeCookie(STORAGE_KEY)
    }
  } else {
    console.warn('[CookieStorage/cbraids] Unknown format, removing')
    removeCookie(STORAGE_KEY)
  }
}

// 커스텀 쿠키 스토리지
const cookieStorage = {
  getItem: (key) => {
    const raw = readCookie(key)
    if (!raw) return null

    // 호환 처리: base64-면 디코드
    if (raw.startsWith('base64-')) {
      try {
        const decoded = atob(raw.slice(7))
        // 디버그
        try {
          const obj = JSON.parse(decoded)
          const rt = obj?.currentSession?.refresh_token ?? obj?.refresh_token
          console.log('[CookieStorage/cbraids] Decoded(base64) refresh_token?', !!rt)
        } catch {}
        return decoded
      } catch {
        console.warn('[CookieStorage/cbraids] Base64 decode failed')
        return null
      }
    }

    // 평문 JSON 체크
    try {
      const obj = JSON.parse(raw)
      const rt = obj?.currentSession?.refresh_token ?? obj?.refresh_token
      console.log('[CookieStorage/cbraids] Session check, refresh_token?', !!rt, 'len:', raw.length)
      return raw
    } catch {
      console.warn('[CookieStorage/cbraids] Value is not JSON')
      return null
    }
  },

  setItem: (key, value) => {
    writeCookie(key, value)
    // 디버그
    try {
      const obj = JSON.parse(value)
      const rt = obj?.currentSession?.refresh_token ?? obj?.refresh_token
      console.log('[CookieStorage/cbraids] Set refresh_token?', !!rt, 'len:', value.length)
    } catch {
      console.log('[CookieStorage/cbraids] Set cookie', key, 'len:', value.length)
    }
  },

  removeItem: (key) => removeCookie(key),
}

// ✅ HMR-safe singleton: 전역 보관소 사용
export function getSupabase() {
  if (globalThis.__sb_cbraids__) {
    console.log('[Supabase/cbraids] Returning existing instance')
    return globalThis.__sb_cbraids__
  }

  console.log('[Supabase/cbraids] Creating new instance')

  // 초기화 전 정리
  normalizeAuthCookieOnce()
  cleanupLegacyKeys()

  const client = createSupabaseClient(URL, KEY, {
    db: { schema: 'public' }, // 필요 시 스키마 변경
    auth: {
      flowType: 'pkce',
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false, // auth 포털이 아니므로 false
      storage: cookieStorage,
      storageKey: STORAGE_KEY,
    },
  })

  globalThis.__sb_cbraids__ = client
  console.log('✅ Supabase client initialized (cbraids)')

  // 초기 세션 체크 (디버깅)
  if (typeof window !== 'undefined') {
    client.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('[Supabase/cbraids] Initial session check error:', error)
      } else {
        console.log('[Supabase/cbraids] Initial session status:', session ? 'Active' : 'None')
        if (session?.expires_at) {
          const expiresIn = new Date(session.expires_at * 1000).getTime() - Date.now()
          console.log('[Supabase/cbraids] Session expires in:', Math.round(expiresIn / 1000 / 60), 'minutes')
        }
      }
    })

    // 수동 테스트용
    window.refreshSessionRaids = async () => {
      const { data, error } = await client.auth.refreshSession()
      console.log('[Manual Refresh/cbraids] Result:', { session: !!data?.session, error })
      return data?.session
    }

    window.checkCookieSizeRaids = () => {
      const key = STORAGE_KEY
      const raw = document.cookie.split('; ').find(c => c.startsWith(key + '='))?.split('=')[1] || ''
      const decoded = decodeURIComponent(raw)
      console.log('Cookie size (chars):', decoded.length)
      try {
        const obj = JSON.parse(decoded)
        console.log('Has refresh_token?', !!(obj?.currentSession?.refresh_token || obj?.refresh_token))
        console.log('Session structure:', Object.keys(obj || {}))
      } catch {
        console.warn('JSON parse failed - cookie may be truncated or base64')
      }
    }
  }

  return client
}

// 기존 코드와의 호환성을 위한 export
export const supabase = getSupabase()